import { set } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { didCancel } from 'ember-concurrency';
import type { Hotel, User } from 'frontend-utils/models';
import { HOTEL_COOKIE_NAME } from 'frontend-utils/utils/constants/constant-values';
import type HotelController from 'lumen/controllers/hotel';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { DRAWER_IDS } from 'lumen/utils/constants/general-constants';
import { getFeaturesForRoute } from 'lumen/utils/constants/navigation';
import { SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY } from 'lumen/utils/constants/smart-insights';
import trialModalAutoOpen from 'lumen/utils/hotel-and-market/trial-modal-auto-open';
type Model = Hotel | undefined;

type Params = {
  id: string;
};

export default class HotelRoute extends Route {
  queryParams = {
    smartInsightsDay: {
      refreshModel: false,
    },
    smartInsightsExpanded: {
      refreshModel: false,
    },
    smartInsightsView: {
      refreshModel: false,
    },
    smartInsightsKpiComparisonPeriod: {
      refreshModel: false,
    },
    smartInsightsPriorities: {
      refreshModel: false,
    },
    smartInsightsTab: {
      refreshModel: false,
    },
    /* Intercom checklist param declared here so it so it survives across all hotel subroutes (e.g. rates) without the need to declare it in each of the subroutes */
    intercomChecklistId: {
      refreshModel: false,
      as: 'intercom_checklist_id',
    },
  };

  @service declare access: Services['access'];
  @service declare cookie: Services['cookie'];
  @service declare drawer: Services['drawer'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare localStorage: Services['local-storage'];
  @service declare navigation: Services['navigation'];
  @service declare productState: Services['product-state'];
  @service declare roiTrophies: Services['roi-trophies'];
  @service declare router: Services['router'];
  @service declare satismeter: Services['satismeter'];
  @service declare smartInsights: Services['smart-insights'];
  @service('smart-insights/upsell')
  declare smartInsightsUpsell: Services['smart-insights/upsell'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare usersnap: Services['usersnap'];

  autoOpenTrialModal: (() => void) | null = null;
  hasSetQueryParametersFromLocalStorage = false;

  afterModel(hotel: Model, transition: Transition): void {
    this.satismeter.sendTraits();

    if (hotel?.id) {
      set(this, 'hotelAndMarketService.loading', false);

      this.hotelAndMarketService.checkPaymentsInfo();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hotelCookie = this.cookie.getCookie(HOTEL_COOKIE_NAME);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (hotelCookie?.id !== hotel.id) {
        // @ts-expect-error: Incorrect type
        this.cookie.setCookie(HOTEL_COOKIE_NAME, {
          id: hotel.id,
          type: 'hotel',
        });
      }

      this.intercom.updateHotel();

      const { user } = this.hotelAndMarketService;

      void this.usersnap.initialise(user);

      // Only get the feature toggles that are enabled.
      const featureToggles = this.featureToggle.getFeatureToggles() ?? {};
      const enabledFeatureToggles = Object.keys(featureToggles).filter(
        (key) => featureToggles[key] === true,
      );

      // Call `identify` after the feature toggle service is initialised, so we can pass the feature toggles as trait.
      this.usageAnalytics.identify(user.id!, {
        featureToggles: enabledFeatureToggles,
        hotelName: hotel.hotelinfo.name,
        hotelinfoId: hotel.hotelinfo.id,
        isStaffUser: this.access.isStaffUser,
        subscriptionId: hotel.id,
      });

      if (this.access.hasSmartInsights) {
        // Perform the smart insights task without blocking afterModel
        void this.fetchSmartInsights(transition);
      }

      if (this.access.hasSmartInsights && this.access.hasSmartInsightsUpsell) {
        void this.fetchSmartInsightsUpsell();
      }

      if (this.access.hasTrophyHighlights) {
        void this.roiTrophies.fetchHighlightTask.perform();
      }
    }
  }

  beforeModel(): void {
    set(this.hotelAndMarketService, 'loading', true);
    this.hotelAndMarketService.clearStore();
    this.drawer.collapseDrawer(DRAWER_IDS.SMART_INSIGHTS_DRAWER);

    if (this.access.hasTrophyHighlights) {
      this.roiTrophies.cleanUp();
    }
  }

  deactivate(): void {
    void this.usersnap.terminate();
  }

  async fetchSmartInsights(transition: Transition): Promise<void> {
    if (!this.hasSetQueryParametersFromLocalStorage) {
      this.setSmartInsightsLocalStorageQueryParams(transition);
    }

    try {
      await this.smartInsights.getSmartInsightsTask.perform();
    } catch (e) {
      // Ignore ember concurrency task cancellation errors because we want to cancel existing tasks when a new task is started.
      if (!didCancel(e)) {
        // But re-throw the non-cancelation error
        throw e;
      }
    }
  }

  async fetchSmartInsightsUpsell(): Promise<void> {
    try {
      await this.smartInsightsUpsell.upsellApiResponseTask.perform();
    } catch {
      // The task already reports its own errors, and its status is read from the task itself.
    }
  }

  async initialiseFeatureToggleService(
    user: User,
    hotel: Hotel,
  ): Promise<void> {
    let hotelId: number | undefined;

    if (hotel.id) {
      hotelId = parseInt(hotel.id, 10);
    }

    // @ts-expect-error: Incorrect type
    await this.featureToggle.initialise(user, {
      isStaffUser: this.access.isStaffUser,
      subscriptionId: hotelId,
    });
  }

  async model(params: Params, transition: Transition): Promise<Model> {
    set(this, 'hotelAndMarketService.loading', true);

    try {
      let hotel = this.store.peekRecord<Hotel>('hotel', params.id);

      if (!hotel) {
        const hotels = await this.store.query<Hotel>(
          'hotel',
          { id: params.id },
          { backgroundReload: false },
        );

        hotel = hotels[0]!;
      }

      // Need to set the hotel already since we use it in the featuretoggle service
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.setHotel(hotel, transition.to.queryParams);

      const { user } = this.hotelAndMarketService;

      await this.initialiseFeatureToggleService(user, hotel);

      if (
        this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW)
      ) {
        await this.navigation.load(hotel.hotelinfo.id!);
      }

      return hotel;
    } catch (e) {
      this.errorTracking.captureException(e);

      this.cookie.resetCookie(HOTEL_COOKIE_NAME);
      this.router.transitionTo('application');
    }
  }

  redirect(model: Model, transition: Transition): void {
    // Redirect to a subroute as /hotel itself doesn't serve anything.
    // The accessible tabs are checked by the route selections function in the beforeModel hook.
    if (transition.targetName === 'hotel.index') {
      if (
        this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW)
      ) {
        this.transition.redirectToEnabledTab();
        return;
      }

      if (this.access.hasOverviewDashboardTab) {
        this.router.transitionTo('hotel.overview', model);
      } else {
        this.router.transitionTo('hotel.rates', model);
      }

      return;
    }

    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW,
      ) ||
      this.navigation.isSingleSubscription
    ) {
      return;
    }

    // With UHV, the hotel selector may transition to a route using a subscription
    // that doesn't support the target route's features (e.g. navigating to rates
    // with a parity-only subscription). Detect the matching subscription, or fall
    // back to an enabled tab.
    const targetRoute = transition.to.name;
    const routeFeatures = getFeaturesForRoute(targetRoute);

    if (!routeFeatures) {
      return;
    }

    const subscriptionId = this.navigation.getSubscriptionId(
      routeFeatures,
      targetRoute,
    );

    if (subscriptionId && subscriptionId !== model?.id) {
      const leafParams = Object.values(
        (transition.to.params ?? {}) as Record<string, string>,
      );
      const queryParams = transition.to.queryParams ?? {};
      this.router.transitionTo(targetRoute, subscriptionId, ...leafParams, {
        queryParams,
      });
    } else if (!subscriptionId) {
      this.transition.redirectToEnabledTab(transition);
    }
  }

  resetController(controller: HotelController, isExiting: boolean): void {
    if (isExiting) {
      this.store.unloadAll('smart-insights/smart-insight-day');
      this.store.unloadAll('smart-insights/smart-insight-day-insight-entry');
      this.store.unloadAll(
        'smart-insights/smart-insight-day-recommendation-entry',
      );
    }

    if (this.autoOpenTrialModal) {
      this.router.off('routeDidChange', this.autoOpenTrialModal);
      this.autoOpenTrialModal = null;
    }

    controller.isTrialModalVisible = false;
  }

  setSmartInsightsLocalStorageQueryParams(transition: Transition): void {
    const smartInsightsViewLocalStorage = this.localStorage.getItem(
      SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY,
    );
    if (smartInsightsViewLocalStorage) {
      transition.to.queryParams['smartInsightsView'] =
        smartInsightsViewLocalStorage;
    }

    this.hasSetQueryParametersFromLocalStorage = true;
  }

  setupController(
    controller: HotelController,
    model: Model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    if (
      this.access.hasSmartInsights &&
      (controller.smartInsightsExpanded || controller.smartInsightsDay)
    ) {
      // When `smartInsightsExpanded` or `smartInsightsDay` QP is set, open the drawer so
      // the insight detail view is visible as soon as the insights have finished loading.
      this.drawer.expandDrawer(DRAWER_IDS.SMART_INSIGHTS_DRAWER);

      if (controller.smartInsightsDay) {
        this.smartInsights.fetchArchived(controller.smartInsightsDay);
      }
    }

    // The Ember trial indicator auto-opened the modal itself; the React header has no Ember indicator.
    this.autoOpenTrialModal = (): void => {
      if (
        this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.REACT_HEADER) &&
        trialModalAutoOpen(this.productState)
      ) {
        controller.trialOptions?.onTrialClick();
      }
    };
    this.router.on('routeDidChange', this.autoOpenTrialModal);
  }
}
